import React, { useState } from 'react'
import SuperUserAccountPageUI from '../../../templates/SuperUserAccountPageUI.js'
import IconButtonUI from '../../../widgets/action/IconButtonUI'
import LRTableActionsUI from '../../../widgets/layout/LRTableActionsUI'
import EmptyListUI from '../../../widgets/output/EmptyListUI'
import ClientDataNavUI from '../../../components/superUser/clientData/ClientDataNavUI'
import SpinnerComponentUI from '../../../components/common/SpinnerComponentUI'

const C = {
  bg: '#f7f8fa',
  white: '#ffffff',
  border: '#e4e6ed',
  borderLight: '#f0f1f6',
  text: '#1c1e27',
  textMuted: '#8a8fa8',
  textLight: '#b0b5c8',
  accent: '#1c1e27',
  accentBg: '#f7f8fa',
  accentBorder: '#e4e6ed',
  mono: "'Segoe UI', system-ui, sans-serif",
  sans: "'Segoe UI', system-ui, sans-serif",
}

const FY_MONTH_ORDER = ['Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec', 'Jan', 'Feb', 'Mar']
const ALL_MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
const MONTH_INDEX = { Jan: 0, Feb: 1, Mar: 2, Apr: 3, May: 4, Jun: 5, Jul: 6, Aug: 7, Sep: 8, Oct: 9, Nov: 10, Dec: 11 }

const GSTIN_STATE_MAP = {
  '01': 'Jammu & Kashmir', '02': 'Himachal Pradesh', '03': 'Punjab',
  '04': 'Chandigarh', '05': 'Uttarakhand', '06': 'Haryana',
  '07': 'Delhi', '08': 'Rajasthan', '09': 'Uttar Pradesh',
  '10': 'Bihar', '11': 'Sikkim', '12': 'Arunachal Pradesh',
  '13': 'Nagaland', '14': 'Manipur', '15': 'Mizoram',
  '16': 'Tripura', '17': 'Meghalaya', '18': 'Assam',
  '19': 'West Bengal', '20': 'Jharkhand', '21': 'Odisha',
  '22': 'Chhattisgarh', '23': 'Madhya Pradesh', '24': 'Gujarat',
  '25': 'Daman & Diu', '26': 'Dadra & Nagar Haveli', '27': 'Maharashtra',
  '28': 'Andhra Pradesh', '29': 'Karnataka', '30': 'Goa',
  '31': 'Lakshadweep', '32': 'Kerala', '33': 'Tamil Nadu',
  '34': 'Puducherry', '35': 'Andaman & Nicobar', '36': 'Telangana',
  '37': 'Andhra Pradesh (New)', '38': 'Ladakh', '97': 'Other Territory',
}

function getStateFromGSTIN(gstin) {
  if (!gstin || gstin.length < 2) return null
  return GSTIN_STATE_MAP[gstin.substring(0, 2)] || null
}




function belongsToFY(monthKey, financialYear) {
  if (!financialYear) return true
  var parts = monthKey.split(' ')
  if (parts.length < 2) return false
  var mon = parts[0], year = parseInt(parts[1])
  var isQ4 = mon === 'Jan' || mon === 'Feb' || mon === 'Mar'
  var fyEnd = isQ4 ? year : year + 1
  var expectedFyEnd = parseInt(financialYear.split('-')[1])
  return fyEnd === expectedFyEnd
}

function monthKeyToNum(monthKey) {
  var parts = monthKey.split(' ')
  if (parts.length < 2) return 0
  var mon = parts[0], year = parseInt(parts[1])
  return year * 100 + (MONTH_INDEX[mon] !== undefined ? MONTH_INDEX[mon] + 1 : 0)
}

function getSortedMonthEntries(monthWiseData, financialYear, dateRange) {
  if (!monthWiseData) return []
  return Object.entries(monthWiseData)
    .filter(function (e) {
      if (!belongsToFY(e[0], financialYear)) return false
      if (dateRange && (dateRange.fromYear || dateRange.toYear)) {
        var num = monthKeyToNum(e[0])
        if (dateRange.fromYear && dateRange.fromMonth) {
          var fromNum = parseInt(dateRange.fromYear) * 100 + (MONTH_INDEX[dateRange.fromMonth] + 1)
          if (num < fromNum) return false
        }
        if (dateRange.toYear && dateRange.toMonth) {
          var toNum = parseInt(dateRange.toYear) * 100 + (MONTH_INDEX[dateRange.toMonth] + 1)
          if (num > toNum) return false
        }
      }
      return true
    })
    .sort(function (a, b) {
      var partsA = a[0].split(' '), partsB = b[0].split(' ')
      var monA = partsA[0], yearA = parseInt(partsA[1] || 0)
      var monB = partsB[0], yearB = parseInt(partsB[1] || 0)
      var isQ4A = monA === 'Jan' || monA === 'Feb' || monA === 'Mar'
      var isQ4B = monB === 'Jan' || monB === 'Feb' || monB === 'Mar'
      var fyEndA = isQ4A ? yearA : yearA + 1
      var fyEndB = isQ4B ? yearB : yearB + 1
      if (fyEndA !== fyEndB) return fyEndA - fyEndB
      return FY_MONTH_ORDER.indexOf(monA) - FY_MONTH_ORDER.indexOf(monB)
    })
}

function sumMonthWise(monthWiseData) {
  if (!monthWiseData) return 0
  return Object.values(monthWiseData).reduce(function (acc, v) { return acc + (v || 0) }, 0)
}

function fmtRupee(paise) {
  if (!paise || paise === 0) return '—'
  var r = paise / 100
  if (r >= 10000000) return '₹' + (r / 10000000).toFixed(2) + ' Cr'
  if (r >= 100000) return '₹' + (r / 100000).toFixed(2) + ' L'
  return '₹' + r.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
}

function entityHasDataInRange(entity, financialYear, dateRange) {
  if (!dateRange || (!dateRange.fromYear && !dateRange.toYear)) return false
  var entries = getSortedMonthEntries(entity.monthWiseData, financialYear, dateRange)
  return entries.some(function (e) { return e[1] > 0 })
}

function fyFromMonthKey(monthKey) {
  var parts = monthKey.split(' ')
  if (parts.length < 2) return null
  var mon = parts[0], year = parseInt(parts[1])
  var isQ4 = mon === 'Jan' || mon === 'Feb' || mon === 'Mar'
  var fyEnd = isQ4 ? year : year + 1
  return (fyEnd - 1) + '-' + fyEnd
}


function buildSummaryData(gstReportList) {
  var gstins = []
  var fySet = {}
  var data = {}

  if (!gstReportList) return { gstins: [], fyList: [], data: {} }

  gstReportList.forEach(function (gstReport) {
    var gstin = gstReport.gstin
    if (!gstin) return
    if (gstins.indexOf(gstin) === -1) gstins.push(gstin)
    if (!data[gstin]) data[gstin] = {}

    var allEntities = (gstReport.customerList || []).concat(gstReport.supplierList || [])
    var customerCount = (gstReport.customerList || []).length
    var supplierCount = (gstReport.supplierList || []).length

    allEntities.forEach(function (entity) {
      if (!entity.monthWiseData) return
      Object.entries(entity.monthWiseData).forEach(function (entry) {
        var mk = entry[0], val = entry[1] || 0
        var fy = fyFromMonthKey(mk)
        if (!fy) return
        fySet[fy] = 1
        if (!data[gstin][fy]) {
          data[gstin][fy] = { total: 0, months: {}, customerCount: customerCount, supplierCount: supplierCount }
        }
        if (!data[gstin][fy].months[mk]) data[gstin][fy].months[mk] = 0
        data[gstin][fy].months[mk] += val
        data[gstin][fy].total += val
      })
    })
  })

  var fyList = Object.keys(fySet).sort(function (a, b) {
    return parseInt(a.split('-')[0]) - parseInt(b.split('-')[0])
  })

  return { gstins: gstins, fyList: fyList, data: data }
}


var GSTSummaryPanelUI = function (props) {
  var gstReportList = props.gstReportList
  var summary = buildSummaryData(gstReportList)
  var gstins = summary.gstins
  var fyList = summary.fyList
  var data = summary.data

  var expandedState = useState({})
  var expanded = expandedState[0], setExpanded = expandedState[1]

  function toggleFY(fy) {
    setExpanded(function (prev) {
      var next = Object.assign({}, prev)
      next[fy] = !prev[fy]
      return next
    })
  }

  if (!gstins.length || !fyList.length) return null

  function getMonthsForFY(fy) {
    var mkSet = {}
    gstins.forEach(function (gstin) {
      if (data[gstin] && data[gstin][fy]) {
        Object.keys(data[gstin][fy].months).forEach(function (mk) { mkSet[mk] = 1 })
      }
    })
    return getSortedMonthEntries(mkSet, fy, null).map(function (e) { return e[0] })
  }

  var cellNum = {
    padding: '9px 16px',
    fontSize: '12px',
    fontFamily: C.mono,
    textAlign: 'right',
    whiteSpace: 'nowrap',
    borderBottom: '1px solid ' + C.borderLight,
    borderLeft: '1px solid ' + C.borderLight,
  }

  var cellNumBold = Object.assign({}, cellNum, { fontWeight: 700 })

  return (
    <div style={{
      background: C.white,
      border: '1px solid ' + C.border,
      borderRadius: '10px',
      overflow: 'hidden',
      boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
      marginBottom: '16px',
    }}>
      <div style={{
        padding: '11px 18px',
        background: C.bg,
        borderBottom: '1px solid ' + C.border,
        display: 'flex',
        alignItems: 'center',
        gap: '10px',
      }}>
        <span style={{
          fontSize: '11px',
          fontWeight: 700,
          color: C.textMuted,
          letterSpacing: '0.07em',
          textTransform: 'uppercase',
          fontFamily: C.sans,
        }}>GST Summary — All GSTINs</span>
        <span style={{
          fontSize: '10px',
          color: C.textLight,
          fontFamily: C.sans,
        }}>Click a year row to expand months</span>
      </div>

      <div style={{ overflowX: 'auto' }}>
        <table style={{ borderCollapse: 'collapse', tableLayout: 'auto', minWidth: '100%' }}>
          <thead>
            <tr style={{ background: C.bg }}>
              <th style={{
                padding: '10px 16px',
                fontSize: '10px',
                fontWeight: 700,
                color: C.textLight,
                textAlign: 'left',
                whiteSpace: 'nowrap',
                letterSpacing: '0.08em',
                textTransform: 'uppercase',
                fontFamily: C.sans,
                borderBottom: '2px solid ' + C.border,
                position: 'sticky',
                left: 0,
                background: C.bg,
                zIndex: 3,
                minWidth: '160px',
              }}>Financial Year</th>
              {gstins.map(function (gstin, i) {
                return (
                  <th key={i} style={{
                    padding: '10px 16px',
                    fontSize: '11px',
                    fontWeight: 700,
                    color: C.text,
                    textAlign: 'right',
                    whiteSpace: 'nowrap',
                    fontFamily: C.mono,
                    letterSpacing: '0.02em',
                    borderBottom: '2px solid ' + C.border,
                    borderLeft: '1px solid ' + C.borderLight,
                    background: C.bg,
                  }}>{gstin}</th>
                )
              })}
              <th style={{
                padding: '10px 16px',
                fontSize: '10px',
                fontWeight: 700,
                color: C.accent,
                textAlign: 'right',
                whiteSpace: 'nowrap',
                letterSpacing: '0.06em',
                textTransform: 'uppercase',
                fontFamily: C.sans,
                borderBottom: '2px solid ' + C.border,
                borderLeft: '2px solid ' + C.accentBorder,
                background: C.accentBg,
              }}>Total</th>
            </tr>
          </thead>

          <tbody>
            {fyList.map(function (fy, fyIdx) {
              var isExpanded = !!expanded[fy]
              var fyRowBg = fyIdx % 2 === 0 ? C.white : '#fafbfc'

              var fyGstinTotals = gstins.map(function (gstin) {
                return (data[gstin] && data[gstin][fy]) ? data[gstin][fy].total : 0
              })
              var fyGrandTotal = fyGstinTotals.reduce(function (s, v) { return s + v }, 0)
              var monthKeys = isExpanded ? getMonthsForFY(fy) : []

              return [
                <tr
                  key={fy}
                  onClick={function () { toggleFY(fy) }}
                  style={{ background: fyRowBg, cursor: 'pointer', transition: 'background 0.1s' }}
                  onMouseEnter={function (ev) { ev.currentTarget.style.background = C.accentBg }}
                  onMouseLeave={function (ev) { ev.currentTarget.style.background = fyRowBg }}
                >
                  <td style={{
                    padding: '10px 16px',
                    fontSize: '12.5px',
                    fontFamily: C.sans,
                    fontWeight: 700,
                    color: C.text,
                    whiteSpace: 'nowrap',
                    borderBottom: isExpanded ? 'none' : '1px solid ' + C.borderLight,
                    borderRight: '1px solid ' + C.border,
                    background: fyRowBg,
                    position: 'sticky',
                    left: 0,
                    zIndex: 1,
                  }}>
                    <span style={{
                      display: 'inline-block',
                      width: '16px',
                      fontSize: '10px',
                      color: C.accent,
                      marginRight: '8px',
                      transform: isExpanded ? 'rotate(90deg)' : 'rotate(0deg)',
                      transition: 'transform 0.18s',
                      verticalAlign: 'middle',
                    }}>›</span>
                    {fy}
                  </td>
                  {fyGstinTotals.map(function (total, ci) {
                    return (
                      <td key={ci} style={Object.assign({}, cellNumBold, {
                        color: total > 0 ? C.text : C.textLight,
                        borderBottom: isExpanded ? 'none' : '1px solid ' + C.borderLight,
                      })}>
                        {fmtRupee(total)}
                      </td>
                    )
                  })}
                  <td style={{
                    padding: '10px 16px',
                    fontSize: '12px',
                    fontFamily: C.mono,
                    textAlign: 'right',
                    whiteSpace: 'nowrap',
                    fontWeight: 800,
                    color: fyGrandTotal > 0 ? C.accent : C.textLight,
                    borderBottom: isExpanded ? 'none' : '1px solid ' + C.borderLight,
                    borderLeft: '2px solid ' + C.accentBorder,
                    background: C.accentBg,
                  }}>
                    {fmtRupee(fyGrandTotal)}
                  </td>
                </tr>,

                isExpanded && monthKeys.map(function (mk, mi) {
                  var monthRowBg = mi % 2 === 0 ? '#f5f7ff' : '#eef2ff'
                  var isLast = mi === monthKeys.length - 1
                  var monthGstinVals = gstins.map(function (gstin) {
                    return (data[gstin] && data[gstin][fy] && data[gstin][fy].months[mk]) || 0
                  })
                  var monthRowTotal = monthGstinVals.reduce(function (s, v) { return s + v }, 0)
                  return (
                    <tr key={fy + '-' + mk} style={{ background: monthRowBg }}>
                      <td style={{
                        padding: '7px 16px 7px 40px',
                        fontSize: '11.5px',
                        fontFamily: C.sans,
                        fontWeight: 500,
                        color: C.textMuted,
                        whiteSpace: 'nowrap',
                        borderBottom: isLast ? '2px solid ' + C.border : '1px solid ' + C.accentBorder,
                        borderRight: '1px solid ' + C.border,
                        background: monthRowBg,
                        position: 'sticky',
                        left: 0,
                        zIndex: 1,
                      }}>{mk}</td>
                      {monthGstinVals.map(function (val, ci) {
                        return (
                          <td key={ci} style={Object.assign({}, cellNum, {
                            color: val > 0 ? C.text : C.textLight,
                            fontWeight: val > 0 ? 600 : 400,
                            borderBottom: isLast ? '2px solid ' + C.border : '1px solid ' + C.accentBorder,
                            background: val > 0 ? '#eef2ff' : monthRowBg,
                          })}>
                            {val > 0 ? '₹' + (val / 100).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '—'}
                          </td>
                        )
                      })}
                      <td style={{
                        padding: '7px 16px',
                        fontSize: '12px',
                        fontFamily: C.mono,
                        textAlign: 'right',
                        whiteSpace: 'nowrap',
                        fontWeight: monthRowTotal > 0 ? 700 : 400,
                        color: monthRowTotal > 0 ? C.accent : C.textLight,
                        borderBottom: isLast ? '2px solid ' + C.border : '1px solid ' + C.accentBorder,
                        borderLeft: '2px solid ' + C.accentBorder,
                        background: monthRowTotal > 0 ? C.accentBg : monthRowBg,
                      }}>
                        {fmtRupee(monthRowTotal)}
                      </td>
                    </tr>
                  )
                }),
              ]
            })}
          </tbody>

          <tfoot>
            <tr>
              <td style={{
                padding: '10px 16px',
                fontSize: '11px',
                fontWeight: 700,
                color: C.accent,
                fontFamily: C.sans,
                borderTop: '2px solid ' + C.accentBorder,
                background: C.accentBg,
                position: 'sticky',
                left: 0,
              }}>Grand Total</td>
              {gstins.map(function (gstin, i) {
                var gstinAllTotal = fyList.reduce(function (sum, fy) {
                  return sum + ((data[gstin] && data[gstin][fy]) ? data[gstin][fy].total : 0)
                }, 0)
                return (
                  <td key={i} style={{
                    padding: '10px 16px',
                    fontSize: '12px',
                    fontFamily: C.mono,
                    textAlign: 'right',
                    whiteSpace: 'nowrap',
                    fontWeight: 700,
                    color: gstinAllTotal > 0 ? C.accent : C.textLight,
                    borderTop: '2px solid ' + C.accentBorder,
                    borderLeft: '1px solid ' + C.accentBorder,
                    background: C.accentBg,
                  }}>
                    {fmtRupee(gstinAllTotal)}
                  </td>
                )
              })}
              <td style={{
                padding: '10px 16px',
                fontSize: '13px',
                fontFamily: C.mono,
                textAlign: 'right',
                whiteSpace: 'nowrap',
                fontWeight: 800,
                color: C.accent,
                borderTop: '2px solid ' + C.accentBorder,
                borderLeft: '2px solid ' + C.accentBorder,
                background: C.accentBg,
              }}>
                {fmtRupee(
                  fyList.reduce(function (s, fy) {
                    return s + gstins.reduce(function (ss, gstin) {
                      return ss + ((data[gstin] && data[gstin][fy]) ? data[gstin][fy].total : 0)
                    }, 0)
                  }, 0)
                )}
              </td>
            </tr>
          </tfoot>
        </table>
      </div>
    </div>
  )
}

var selectStyle = {
  height: '34px',
  padding: '0 28px 0 12px',
  border: '1px solid #d5d8e0',
  borderRadius: '6px',
  fontSize: '14px',
  fontFamily: "'Segoe UI', system-ui, sans-serif",
  color: '#1c1e27',
  background: '#ffffff',
  cursor: 'pointer',
  outline: 'none',
  appearance: 'none',
  WebkitAppearance: 'none',
  backgroundImage: "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='10' height='6' viewBox='0 0 10 6'%3E%3Cpath d='M0 0l5 6 5-6z' fill='%238a8fa8'/%3E%3C/svg%3E\")",
  backgroundRepeat: 'no-repeat',
  backgroundPosition: 'right 10px center',
  minWidth: '90px',
}

var DateRangeFilterUI = function (props) {
  var onSubmit = props.onSubmit
  var onClear = props.onClear

  var s1 = useState(''), fromYear = s1[0], setFromYear = s1[1]
  var s2 = useState(''), fromMonth = s2[0], setFromMonth = s2[1]
  var s3 = useState(''), toYear = s3[0], setToYear = s3[1]
  var s4 = useState(''), toMonth = s4[0], setToMonth = s4[1]

  var currentYear = new Date().getFullYear()
  var years = []
  for (var y = currentYear - 5; y <= currentYear + 1; y++) years.push(y)

  function handleSubmit() {
    onSubmit && onSubmit({ fromYear: fromYear, fromMonth: fromMonth, toYear: toYear, toMonth: toMonth })
  }

  function handleClear() {
    setFromYear(''); setFromMonth(''); setToYear(''); setToMonth('')
    onClear && onClear()
  }

  var linkStyle = {
    fontSize: '13px',
    fontWeight: 400,
    fontFamily: C.sans,
    color: '#3b82f6',
    cursor: 'pointer',
    background: 'none',
    border: 'none',
    padding: 0,
    textDecoration: 'none',
  }

  return (
    <div style={{
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'flex-end',
      gap: '10px',
      flexWrap: 'wrap',
      padding: '12px 16px',
    }}>
      <select style={selectStyle} value={fromYear} onChange={function (e) { setFromYear(e.target.value) }}>
        <option value=''>Year</option>
        {years.map(function (y) { return <option key={y} value={y}>{y}</option> })}
      </select>
      <select style={selectStyle} value={fromMonth} onChange={function (e) { setFromMonth(e.target.value) }}>
        <option value=''>Month</option>
        {ALL_MONTHS.map(function (m) { return <option key={m} value={m}>{m}</option> })}
      </select>
      <span style={{ color: C.textMuted, fontSize: '14px' }}>-</span>
      <select style={selectStyle} value={toYear} onChange={function (e) { setToYear(e.target.value) }}>
        <option value=''>Year</option>
        {years.map(function (y) { return <option key={y} value={y}>{y}</option> })}
      </select>
      <select style={selectStyle} value={toMonth} onChange={function (e) { setToMonth(e.target.value) }}>
        <option value=''>Month</option>
        {ALL_MONTHS.map(function (m) { return <option key={m} value={m}>{m}</option> })}
      </select>
      <span onClick={handleSubmit} style={linkStyle}>Submit</span>
      <span onClick={handleClear} style={linkStyle}>Clear</span>
    </div>
  )
}

var CrossTableUI = function (props) {
  var entities = props.entities
  var financialYear = props.financialYear
  var dateRange = props.dateRange

  if (!entities || entities.length === 0) return (
    <div style={{ padding: '32px', textAlign: 'center', color: C.textLight, fontSize: '13px', fontFamily: C.sans }}>
      No data found
    </div>
  )

  var allKeysSet = {}
  entities.forEach(function (e) {
    if (e.monthWiseData) {
      Object.keys(e.monthWiseData).forEach(function (mk) { allKeysSet[mk] = 1 })
    }
  })
  var sortedMonths = getSortedMonthEntries(allKeysSet, financialYear, dateRange).map(function (e) { return e[0] })

  if (sortedMonths.length === 0) return (
    <div style={{ padding: '28px', textAlign: 'center', color: C.textLight, fontSize: '13px', fontFamily: C.sans }}>
      No data for selected range
    </div>
  )

  var entityTotals = entities.map(function (e) {
    return sortedMonths.reduce(function (sum, mk) {
      return sum + ((e.monthWiseData && e.monthWiseData[mk]) || 0)
    }, 0)
  })

  var monthTotals = sortedMonths.map(function (mk) {
    return entities.reduce(function (sum, e) {
      return sum + ((e.monthWiseData && e.monthWiseData[mk]) || 0)
    }, 0)
  })

  var grandTotal = entityTotals.reduce(function (s, v) { return s + v }, 0)

  var thBase = {
    padding: '10px 16px',
    fontSize: '14px',
    fontWeight: 400,
    whiteSpace: 'nowrap',
    fontFamily: C.sans,
    borderBottom: '2px solid ' + C.border,
  }

  return (
    <div style={{ overflowX: 'auto' }}>
      <table style={{ borderCollapse: 'collapse', tableLayout: 'auto', minWidth: '100%' }}>
        <thead>
          <tr>
            <th style={Object.assign({}, thBase, {
              textAlign: 'left',
              color: C.text,
              background: C.bg,
              borderRight: '1px solid ' + C.border,
              position: 'sticky',
              left: 0,
              zIndex: 3,
              minWidth: '110px',
              fontSize: '13px',
            })}>Month</th>
            <th style={Object.assign({}, thBase, {
              textAlign: 'right',
              color: C.text,
              background: C.accentBg,
              borderLeft: '2px solid ' + C.accentBorder,
              fontSize: '13px',
            })}>Total</th>
            {entities.map(function (e, i) {
              var matched = entityHasDataInRange(e, financialYear, dateRange)
              return (
                <th key={i} style={Object.assign({}, thBase, {
                  textAlign: 'right',
                  fontWeight: 400,
                  color: C.text,
                  background: C.bg,
                  borderLeft: '1px solid ' + C.borderLight,
                  fontFamily: C.sans,
                  fontSize: '14px',
                })}>
                  {e.name}
                </th>
              )
            })}
          </tr>
          <tr>
            <td style={{
              padding: '2px 16px 8px',
              background: C.bg,
              borderBottom: '1px solid ' + C.border,
              borderRight: '1px solid ' + C.border,
              position: 'sticky',
              left: 0,
              zIndex: 2,
            }} />
            <td style={{
              background: C.accentBg,
              borderBottom: '1px solid ' + C.border,
              borderLeft: '2px solid ' + C.accentBorder,
            }} />
            {entities.map(function (e, i) {
              var matched = entityHasDataInRange(e, financialYear, dateRange)
              return (
                <td key={i} style={{
                  padding: '2px 16px 8px',
                  fontSize: '10px',
                  color: C.textMuted,
                  textAlign: 'right',
                  fontFamily: C.sans,
                  background: matched ? C.accentBg : C.bg,
                  borderBottom: '1px solid ' + C.border,
                  borderLeft: '1px solid ' + C.borderLight,
                }}>
                  {e.totalInvoiceCount} {e.totalInvoiceCount === 1 ? 'invoice' : 'invoices'}
                </td>
              )
            })}
          </tr>
        </thead>
        <tbody>
          {sortedMonths.map(function (mk, ri) {
            var rowBg = ri % 2 === 0 ? C.white : '#fafbfc'
            var rowTotal = monthTotals[ri]
            return (
              <tr key={mk}>
                <td style={{
                  padding: '8px 16px',
                  fontSize: '14px',
                  fontFamily: C.sans,
                  fontWeight: 400,
                  color: C.textMuted,
                  whiteSpace: 'nowrap',
                  borderBottom: '1px solid ' + C.borderLight,
                  borderRight: '1px solid ' + C.border,
                  background: C.white,
                  position: 'sticky',
                  left: 0,
                  zIndex: 1,
                }}>{mk}</td>
                <td style={{
                  padding: '8px 16px',
                  fontSize: '12px',
                  fontFamily: C.mono,
                  textAlign: 'right',
                  whiteSpace: 'nowrap',
                  fontWeight: 400,
                  color: C.text,
                  borderBottom: '1px solid ' + C.borderLight,
                  borderLeft: '2px solid ' + C.border,
                  background: C.bg,
                }}>
                  {fmtRupee(rowTotal)}
                </td>
                {entities.map(function (e, ci) {
                  var amount = (e.monthWiseData && e.monthWiseData[mk]) || 0
                  return (
                    <td key={ci} style={{
                      padding: '8px 16px',
                      fontSize: '14px',
                      fontFamily: C.mono,
                      textAlign: 'right',
                      whiteSpace: 'nowrap',
                      color: C.text,
                      fontWeight: 400,
                      borderBottom: '1px solid ' + C.borderLight,
                      borderLeft: '1px solid ' + C.borderLight,
                      background: C.white,
                    }}>
                      {amount !== 0 ? (amount < 0 ? '-₹' + Math.abs(amount / 100).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '₹' + (amount / 100).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })) : '—'}
                    </td>
                  )
                })}
              </tr>
            )
          })}
        </tbody>
        <tfoot>
          <tr>
            <td style={{
              padding: '10px 16px',
              fontSize: '11px',
              fontWeight: 700,
              color: C.accent,
              fontFamily: C.sans,
              borderTop: '2px solid ' + C.accentBorder,
              background: C.accentBg,
              position: 'sticky',
              left: 0,
            }}>Total</td>
            <td style={{
              padding: '10px 16px',
              fontSize: '13px',
              fontFamily: C.mono,
              textAlign: 'right',
              whiteSpace: 'nowrap',
              fontWeight: 400,
              color: C.text,
              borderTop: '2px solid ' + C.border,
              borderLeft: '2px solid ' + C.border,
              background: C.bg,
            }}>
              {fmtRupee(grandTotal)}
            </td>
            {entityTotals.map(function (total, i) {
              return (
                <td key={i} style={{
                  padding: '10px 16px',
                  fontSize: '12px',
                  fontFamily: C.mono,
                  textAlign: 'right',
                  whiteSpace: 'nowrap',
                  fontWeight: 400,
                  color: C.text,
                  borderTop: '2px solid ' + C.border,
                  borderLeft: '1px solid ' + C.border,
                  background: C.bg,
                }}>
                  {fmtRupee(total)}
                </td>
              )
            })}
          </tr>
        </tfoot>
      </table>
    </div>
  )
}

function entryHasMonthWise(g) {
  var entities = (g.customerList || []).concat(g.supplierList || [])
  return entities.some(function (e) {
    return e.monthWiseData && Object.keys(e.monthWiseData).length > 0
  })
}

function formatPeriod(g) {
  if (g.periodFrom && g.periodTo) return g.periodFrom + ' – ' + g.periodTo
  return null
}

var OldStyleTableUI = function (props) {
  var entities = props.entities
  var th = {
    padding: '10px 16px',
    fontSize: '13px',
    fontWeight: 400,
    whiteSpace: 'nowrap',
    fontFamily: C.sans,
    color: C.text,
    background: C.bg,
    borderBottom: '2px solid ' + C.border,
  }
  var td = {
    padding: '8px 16px',
    fontSize: '14px',
    fontFamily: C.sans,
    color: C.text,
    borderBottom: '1px solid ' + C.borderLight,
  }
  return (
    <div>
      <div style={{ overflowX: 'auto' }}>
        <table style={{ borderCollapse: 'collapse', width: '100%' }}>
          <thead>
            <tr>
              <th style={Object.assign({}, th, { textAlign: 'left', width: '6%' })}>Sr. No.</th>
              <th style={Object.assign({}, th, { textAlign: 'left' })}>Name</th>
              <th style={Object.assign({}, th, { textAlign: 'right', width: '18%' })}>Invoices</th>
              <th style={Object.assign({}, th, { textAlign: 'right', width: '24%' })}>Amount</th>
            </tr>
          </thead>
          <tbody>
            {entities.map(function (e, i) {
              return (
                <tr key={i}>
                  <td style={td}>{i + 1}</td>
                  <td style={td}>{e.name}</td>
                  <td style={Object.assign({}, td, { textAlign: 'right' })}>{e.totalInvoiceCount}</td>
                  <td style={Object.assign({}, td, { textAlign: 'right', fontFamily: C.mono })}>{e.totalTaxableAmountF2}</td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>
    </div>
  )
}
function aggregateSpansMultipleFYs(g) {
  var from = parsePeriodNum(g.aggregatePeriodFrom)
  var to = parsePeriodNum(g.aggregatePeriodTo)
  if (!from || !to) return false
  var fyStart = function (n) {
    var y = Math.floor(n / 100), m = n % 100
    return m >= 4 ? y : y - 1
  }
  return fyStart(from) !== fyStart(to)
}

function hasAggregateData(g) {
  if (aggregateSpansMultipleFYs(g)) return false
  return !!((g.aggregateCustomerList && g.aggregateCustomerList.length) ||
    (g.aggregateSupplierList && g.aggregateSupplierList.length))
}

function parsePeriodNum(label) {
  if (!label) return null
  var parts = String(label).trim().split(' ')
  if (parts.length < 2) return null
  var mi = MONTH_INDEX[parts[0].substring(0, 3)]
  var yr = parseInt(parts[1])
  if (mi === undefined || isNaN(yr)) return null
  return yr * 100 + mi + 1
}

function getMonthWiseRange(g) {
  var keySet = {}
    ; (g.customerList || []).concat(g.supplierList || []).forEach(function (e) {
      if (e.monthWiseData) Object.keys(e.monthWiseData).forEach(function (k) { keySet[k] = 1 })
    })
  var keys = getSortedMonthEntries(keySet, g.financialYear, null).map(function (e) { return e[0] })
  if (!keys.length) return null
  return { first: keys[0], last: keys[keys.length - 1], count: keys.length }
}

var AggregateSectionUI = function (props) {
  var g = props.gstReports
  var list = props.list
  var period = (g.aggregatePeriodFrom && g.aggregatePeriodTo)
    ? g.aggregatePeriodFrom + ' – ' + g.aggregatePeriodTo : 'Aggregate'
  return (
    <div>
      <div style={{
        padding: '10px 18px',
        background: C.bg,
        borderBottom: '1px solid ' + C.border,
        fontFamily: C.sans,
        fontSize: '12px',
        color: C.textMuted,
      }}>
        <span style={{ fontSize: '14px', color: C.text, marginRight: '10px' }}>{period}</span>
        Aggregate for {period}. Month-wise breakdown is not available for this report version.{' · ' + list.length + ' ' + (list.length === 1 ? props.label.slice(0, -1) : props.label).toLowerCase()}
      </div>
      {list.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '24px 0', color: C.textLight, fontSize: '13px', fontFamily: C.sans }}>
          No {props.label.toLowerCase()} in the aggregate
        </div>
      ) : (
        <OldStyleTableUI entities={list} />
      )}
    </div>
  )
}

var MonthWiseBannerUI = function (props) {
  var g = props.gstReports
  var range = props.range
  return (
    <div style={{
      padding: '10px 18px',
      background: C.bg,
      borderTop: '1px solid ' + C.border,
      borderBottom: '1px solid ' + C.border,
      fontFamily: C.sans,
      fontSize: '12px',
      color: C.textMuted,
    }}>
      <span style={{ fontSize: '14px', color: C.text, marginRight: '10px' }}>Month-wise</span>
      Available: {range.first} – {range.last} ({range.count} month{range.count > 1 ? 's' : ''}){' · ' + props.count + ' ' + (props.count === 1 ? props.label.slice(0, -1) : props.label).toLowerCase()}
    </div>
  )
}

var DataViewSelectorUI = function (props) {
  var options = props.options
  var selected = props.selected
  var onSelect = props.onSelect
  return (
    <div style={{
      padding: '10px 18px',
      background: C.bg,
      borderBottom: '1px solid ' + C.border,
      fontFamily: C.sans,
    }}>
      <div style={{ fontSize: '12px', color: C.textMuted, marginBottom: '8px' }}>
        Aggregate and month-wise data are both available for this financial year. Select the data to view.
      </div>
      {options.map(function (opt) {
        var isSelected = selected === opt.id
        return (
          <div
            key={opt.id}
            onClick={function () { onSelect(isSelected ? null : opt.id) }}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '10px',
              padding: '8px 16px',
              marginBottom: '6px',
              background: C.white,
              border: '1px solid ' + (isSelected ? '#3b82f6' : C.border),
              borderRadius: '8px',
              cursor: 'pointer',
            }}
          >
            <span style={{
              display: 'inline-block',
              width: '12px',
              fontSize: '10px',
              color: C.text,
              transform: isSelected ? 'rotate(90deg)' : 'rotate(0deg)',
              transition: 'transform 0.18s',
            }}>›</span>
            <span style={{ fontSize: '14px', color: isSelected ? '#3b82f6' : C.text }}>{opt.title}</span>
            <span style={{ fontSize: '13px', color: C.text }}>{opt.period}</span>
            <span style={{ fontSize: '12px', color: C.textMuted }}>{opt.detail}</span>
            <span style={{ fontSize: '12px', color: C.textLight, marginLeft: 'auto' }}>
              {isSelected ? 'Click to hide' : 'Click to view'}
            </span>
          </div>
        )
      })}
    </div>
  )
}

var GSTRecordsUI = function (props) {
  var gstReports = props.gstReports
  var gstReportMonthlyList = props.gstReportMonthlyList
  var dateRange = props.dateRange

  var s = useState('customers')
  var activeTab = s[0], setActiveTab = s[1]
  var vs = useState(null)
  var selectedView = vs[0], setSelectedView = vs[1]
  var customerList = gstReports.customerList || []
  var supplierList = gstReports.supplierList || []

  var isOldStyle = !entryHasMonthWise(gstReports)
  var hasAggregate = false // aggregates are listed in the bottom section, not inside an FY card
  var aggregateList = activeTab === 'customers'
    ? (gstReports.aggregateCustomerList || [])
    : (gstReports.aggregateSupplierList || [])
  var monthRange = getMonthWiseRange(gstReports)
  var hasBoth = hasAggregate && !!monthRange
  var showMonthWise = !hasBoth || selectedView === 'monthwise'
  var tabs = [
    { id: 'customers', label: 'Customers', tabLabel: 'Top Customers', count: customerList.length, aggCount: (gstReports.aggregateCustomerList || []).length, list: customerList },
    { id: 'suppliers', label: 'Suppliers', tabLabel: 'Top Suppliers', count: supplierList.length, aggCount: (gstReports.aggregateSupplierList || []).length, list: supplierList },
  ]

  var active = null
  for (var t = 0; t < tabs.length; t++) {
    if (tabs[t].id === activeTab) { active = tabs[t]; break }
  }

  var sortedList = active.list.slice()
  if (dateRange && (dateRange.fromYear || dateRange.toYear)) {
    sortedList.sort(function (a, b) {
      var aMatch = entityHasDataInRange(a, gstReports.financialYear, dateRange) ? 0 : 1
      var bMatch = entityHasDataInRange(b, gstReports.financialYear, dateRange) ? 0 : 1
      return aMatch - bMatch
    })
  }

  return (
    <div style={{ marginBottom: '12px' }}>
      <div style={{
        background: C.white,
        border: '1px solid ' + C.border,
        borderRadius: '10px',
        overflow: 'hidden',
        boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
      }}>

        <div style={{
          padding: '10px 18px 0',
          borderBottom: '1px solid ' + C.border,
          background: C.white,
        }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '10px',
            marginBottom: '10px',
          }}>
            <span style={{
              fontSize: '15px',
              fontWeight: 400,
              fontFamily: C.sans,
              color: C.text,
            }}>GST number: {gstReports.gstin}</span>
            {getStateFromGSTIN(gstReports.gstin) && (
              <span style={{
                fontSize: '11px',
                fontWeight: 600,
                color: C.textMuted,
                fontFamily: C.sans,
                background: C.bg,
                border: '1px solid ' + C.border,
                borderRadius: '4px',
                padding: '2px 8px',
                marginLeft: '4px',
              }}>{getStateFromGSTIN(gstReports.gstin)}</span>
            )}
          </div>
          <div style={{ display: 'flex', gap: '2px' }}>
            {tabs.map(function (tab) {
              var isActive = activeTab === tab.id
              return (
                <button
                  key={tab.id}
                  onClick={function () { setActiveTab(tab.id) }}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '7px',
                    padding: '8px 16px',
                    border: 'none',
                    borderRadius: '6px 6px 0 0',
                    cursor: 'pointer',
                    fontFamily: C.sans,
                    fontSize: '13px',
                    fontWeight: 600,
                    position: 'relative',
                    marginBottom: '-1px',
                    zIndex: isActive ? 2 : 1,
                    background: isActive ? C.white : 'transparent',
                    color: isActive ? C.accent : C.textMuted,
                    borderTop: isActive ? '2px solid ' + C.accent : '2px solid transparent',
                    borderLeft: isActive ? '1px solid ' + C.border : '1px solid transparent',
                    borderRight: isActive ? '1px solid ' + C.border : '1px solid transparent',
                    borderBottom: isActive ? '1px solid ' + C.white : '1px solid transparent',
                  }}
                >
                  {tab.tabLabel}
                  <span style={{
                    color: C.textMuted,
                    fontSize: '13px',
                    fontWeight: 600,
                  }}>
                    {(hasAggregate && monthRange) ? '' : tab.count}
                  </span>
                </button>
              )
            })}
          </div>
        </div>

        {hasBoth && (
          <DataViewSelectorUI
            selected={selectedView}
            onSelect={setSelectedView}
            options={[
              {
                id: 'aggregate',
                title: 'Aggregate',
                period: (gstReports.aggregatePeriodFrom && gstReports.aggregatePeriodTo)
                  ? gstReports.aggregatePeriodFrom + ' – ' + gstReports.aggregatePeriodTo : 'Aggregate period',
                detail: 'No month-wise breakdown · ' + aggregateList.length + ' '
                  + (aggregateList.length === 1 ? active.label.slice(0, -1) : active.label).toLowerCase(),
              },
              {
                id: 'monthwise',
                title: 'Month-wise',
                period: monthRange.first + ' – ' + monthRange.last,
                detail: monthRange.count + ' month' + (monthRange.count > 1 ? 's' : '') + ' · ' + sortedList.length + ' '
                  + (sortedList.length === 1 ? active.label.slice(0, -1) : active.label).toLowerCase(),
              },
            ]}
          />
        )}
        {hasAggregate && (!hasBoth || selectedView === 'aggregate') && (
          <AggregateSectionUI gstReports={gstReports} list={aggregateList} label={active.label} />
        )}
        {hasBoth && selectedView === 'monthwise' && (
          <MonthWiseBannerUI gstReports={gstReports} range={monthRange} count={sortedList.length} label={active.label} />
        )}

        {!showMonthWise ? null : sortedList.length === 0 ? (
          <div style={{
            textAlign: 'center',
            padding: '32px 0',
            color: C.textLight,
            fontSize: '13px',
            fontFamily: C.sans,
          }}>
            No {active.label.toLowerCase()} found
          </div>
        ) : (
          isOldStyle ? (
            <OldStyleTableUI entities={sortedList} gstReports={gstReports} />
          ) : (
            <CrossTableUI
              entities={sortedList}
              financialYear={gstReports.financialYear}
              dateRange={dateRange}
            />
          )
        )}
      </div>

    </div>
  )
}


var GSTReportPageUI = function (props) {
  var context = props.context
  var account = props.account
  var gstReportList = props.gstReportList
  var gstReportMonthlyList = props.gstReportMonthlyList
  var onRefreshClick = props.onRefreshClick

  var ds = useState(null)
  var dateRange = ds[0], setDateRange = ds[1]

  var MONTHLY_KEY = '__MONTHLY_SUMMARY__'

  var fyExpandState = useState(null)
  var expandedFY = fyExpandState[0], setExpandedFY = fyExpandState[1]

  var hasInteractedState = useState(false)
  var hasInteractedWithFY = hasInteractedState[0], setHasInteractedWithFY = hasInteractedState[1]

  function toggleFYSection(fy) {
    setHasInteractedWithFY(true)
    setExpandedFY(function (prev) {
      return prev === fy ? null : fy
    })
  }

  var monthlySummaryExpanded = expandedFY === MONTHLY_KEY
  var gstinExpandState = useState({})
  var expandedGstins = gstinExpandState[0], setExpandedGstins = gstinExpandState[1]

  var viewMoreYearsState = useState({})
  var viewMoreYears = viewMoreYearsState[0], setViewMoreYears = viewMoreYearsState[1]

  function toggleViewMoreYears(key) {
    setViewMoreYears(function (prev) {
      var next = Object.assign({}, prev)
      next[key] = !prev[key]
      return next
    })
  }
  function toggleGstinRow(key) {
    setExpandedGstins(function (prev) {
      var next = Object.assign({}, prev)
      next[key] = !prev[key]
      return next
    })
  }

  function getFYFromDateISO(dateISO) {
    var d = new Date(dateISO)
    var year = d.getFullYear()
    var month = d.getMonth() + 1
    if (month >= 4) {
      return year + '-' + (year + 1)
    } else {
      return (year - 1) + '-' + year
    }
  }

  var uniqueGstins = gstReportList ? gstReportList.reduce(function (acc, g) {
    if (acc.indexOf(g.gstin) === -1) acc.push(g.gstin)
    return acc
  }, []) : []

  var selectedGstinsState = useState(null)
  var selectedGstins = selectedGstinsState[0], setSelectedGstins = selectedGstinsState[1]

  function toggleGstinSelection(gstin) {
    setSelectedGstins(function (prev) {
      if (prev === null) return [gstin]
      var idx = prev.indexOf(gstin)
      if (idx === -1) {
        return prev.concat([gstin])
      } else {
        var next = prev.filter(function (g) { return g !== gstin })
        return next.length === 0 ? null : next
      }
    })
  }

  function isGstinSelected(gstin) {
    return selectedGstins === null || selectedGstins.indexOf(gstin) !== -1
  }

  var filteredGstReportList = gstReportList ? gstReportList.filter(function (g) { return isGstinSelected(g.gstin) }) : []
  var filteredGstReportMonthlyList = gstReportMonthlyList ? gstReportMonthlyList.filter(function (r) { return isGstinSelected(r.gstin) }) : []

  return (
    <SuperUserAccountPageUI
      context={context}
      className='pg_su_gst'
      backLink='/su/accounts'
      headerText={account && account.accountName}
      centered={!gstReportList}
      account={account}
      page='clientData'
      dimension='w1024'
    >
      {!gstReportList && <SpinnerComponentUI />}

      {gstReportList && (
        <div style={{ position: 'relative' }}>
          <LRTableActionsUI
            location='top'
            left={
              <div className='g_flex_h'>
                <IconButtonUI icon='icon-refresh' onClick={onRefreshClick} />
                <span className='g_m_left_100 g_large'><b>Client Data - GST</b></span>
              </div>
            }
            right={<span />}
          />

          {account && <ClientDataNavUI account={account} page='gst' />}



          <DateRangeFilterUI
            onSubmit={function (range) { setDateRange(range) }}
            onClear={function () { setDateRange(null) }}
          />

          <div style={{ marginTop: '4px' }}>
            {!gstReportList.length && (
              <EmptyListUI
                className='g_m_top_200'
                context={context}
                createDisabledMessageLabel='No GST data'
              />
            )}
            {(function () {
              // Group by financialYear so FY 2025-2026 shows all GSTINs together,

              var fyMap = {}
              filteredGstReportList.forEach(function (gstReports) {
                if (!entryHasMonthWise(gstReports)) return
                var fy = gstReports.financialYear || 'Unknown'
                if (!fyMap[fy]) fyMap[fy] = []
                fyMap[fy].push(gstReports)
              })
              // Newest FY first
              var sortedFYs = Object.keys(fyMap).sort(function (a, b) {
                return parseInt(b.split('-')[0]) - parseInt(a.split('-')[0])
              })
              return sortedFYs.map(function (fy, fyIndex) {
                var isLatestFY = fyIndex === 0
                var effectiveExpandedFY = expandedFY === null ? sortedFYs[0] : expandedFY
                var isExpanded = fy === effectiveExpandedFY

                return (
                  <div key={fy} style={{ marginBottom: '24px' }}>
                    {/* FY group header */}
                    <div
                      onClick={function () { toggleFYSection(fy) }}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '10px',
                        padding: '8px 16px',
                        marginBottom: '8px',
                        background: C.accentBg,
                        border: '1px solid ' + C.accentBorder,
                        borderRadius: '8px',
                        cursor: isLatestFY ? 'default' : 'pointer',
                      }}
                    >
                      {!isLatestFY && (
                        <span style={{
                          display: 'inline-block',
                          width: '12px',
                          fontSize: '10px',
                          color: C.accent,
                          transform: isExpanded ? 'rotate(90deg)' : 'rotate(0deg)',
                          transition: 'transform 0.18s',
                        }}>›</span>
                      )}
                      <span style={{
                        fontSize: '15px',
                        fontWeight: 400,
                        color: C.text,
                        fontFamily: C.sans,
                      }}>{fy}</span>
                      <span style={{
                        fontSize: '10px',
                        fontWeight: 400,
                        color: C.textLight,
                        fontFamily: C.sans,
                      }}>{fyMap[fy].length} GSTIN{fyMap[fy].length > 1 ? 's' : ''}</span>
                      {!isLatestFY && (
                        <span style={{
                          fontSize: '10px',
                          color: C.textLight,
                          fontFamily: C.sans,
                          marginLeft: 'auto',
                        }}>{isExpanded ? 'Click to collapse' : 'Click to expand'}</span>
                      )}
                    </div>

                    {isExpanded && ((isLatestFY && !hasInteractedWithFY) ? (
                      fyMap[fy].map(function (gstReports, i) {
                        return (
                          <GSTRecordsUI
                            key={gstReports.gstin || i}
                            context={context}
                            account={account}
                            gstReports={gstReports}
                            gstReportMonthlyList={[]}
                            dateRange={dateRange}
                          />
                        )
                      })
                    ) : (
                      fyMap[fy].map(function (gstReports, i) {
                        var gstinKey = fy + '|' + gstReports.gstin
                        var isGstinExpanded = !!expandedGstins[gstinKey]
                        return (
                          <div key={gstReports.gstin || i} style={{ marginBottom: '8px' }}>
                            <div
                              onClick={function () { toggleGstinRow(gstinKey) }}
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '10px',
                                padding: '8px 16px',
                                background: C.white,
                                border: '1px solid ' + C.border,
                                borderRadius: '8px',
                                cursor: 'pointer',
                              }}
                            >
                              <span style={{
                                display: 'inline-block',
                                width: '12px',
                                fontSize: '10px',
                                color: C.text,
                                transform: isGstinExpanded ? 'rotate(90deg)' : 'rotate(0deg)',
                                transition: 'transform 0.18s',
                              }}>›</span>
                              <span style={{
                                fontSize: '14px',
                                fontWeight: 400,
                                color: C.text,
                                fontFamily: C.sans,
                              }}>GST number: {gstReports.gstin}</span>                              <span style={{
                                fontSize: '12px',
                                color: C.textLight,
                                fontFamily: C.sans,
                                marginLeft: 'auto',
                              }}>{isGstinExpanded ? 'Click to collapse' : 'Click to expand'}</span>
                            </div>
                            {isGstinExpanded && (
                              <div style={{ marginTop: '8px' }}>
                                <GSTRecordsUI
                                  context={context}
                                  account={account}
                                  gstReports={gstReports}
                                  gstReportMonthlyList={[]}
                                  dateRange={dateRange}
                                />
                              </div>
                            )}
                          </div>
                        )
                      })
                    ))}
                  </div>
                )
              })
            })()}
            {(function () {
              var seen = {}
              var oldEntries = filteredGstReportList.filter(function (g) {
                if (entryHasMonthWise(g)) return false
                var k = g.gstin + '|' + (g.periodFrom || '') + '|' + (g.periodTo || '')
                if (seen[k]) return false
                seen[k] = 1
                return true
              })
             // aggregates kept on month-wise rows (any FY span) are listed here too
filteredGstReportList.forEach(function (g) {
  if (!entryHasMonthWise(g)) return
  if (!((g.aggregateCustomerList && g.aggregateCustomerList.length) ||
    (g.aggregateSupplierList && g.aggregateSupplierList.length))) return
  var k = g.gstin + '|' + (g.aggregatePeriodFrom || '') + '|' + (g.aggregatePeriodTo || '')
  if (seen[k]) return
  seen[k] = 1
  oldEntries.push({
    gstin: g.gstin,
    periodFrom: g.aggregatePeriodFrom,
    periodTo: g.aggregatePeriodTo,
    customerList: g.aggregateCustomerList || [],
    supplierList: g.aggregateSupplierList || []
  })
})
if (!oldEntries.length) return null
              var byPeriod = {}
              oldEntries.forEach(function (g) {
                var pk = formatPeriod(g) || 'Older report'
                if (!byPeriod[pk]) byPeriod[pk] = []
                byPeriod[pk].push(g)
              })
              return Object.keys(byPeriod).map(function (pk) {
                var groupKey = 'old|' + pk
                var isGroupExpanded = !!expandedGstins[groupKey]
                return (
                  <div key={groupKey} style={{ marginBottom: '24px' }}>
                    <div
                      onClick={function () { toggleGstinRow(groupKey) }}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '10px',
                        padding: '8px 16px',
                        marginBottom: '8px',
                        background: C.accentBg,
                        border: '1px solid ' + C.accentBorder,
                        borderRadius: '8px',
                        cursor: 'pointer',
                      }}
                    >
                      <span style={{
                        display: 'inline-block',
                        width: '12px',
                        fontSize: '10px',
                        color: C.accent,
                        transform: isGroupExpanded ? 'rotate(90deg)' : 'rotate(0deg)',
                        transition: 'transform 0.18s',
                      }}>›</span>
                      <span style={{ fontSize: '15px', fontWeight: 400, color: C.text, fontFamily: C.sans }}>{pk}</span>
                      <span style={{ fontSize: '12px', color: C.textMuted, fontFamily: C.sans }}>
                        Aggregate for {pk === 'Older report' ? 'the whole report period' : pk}, month-wise breakdown is not available for this report version · {byPeriod[pk].length} GSTIN{byPeriod[pk].length > 1 ? 's' : ''}
                      </span>
                      <span style={{
                        fontSize: '10px',
                        color: C.textLight,
                        fontFamily: C.sans,
                        marginLeft: 'auto',
                        whiteSpace: 'nowrap',
                      }}>{isGroupExpanded ? 'Click to collapse' : 'Click to expand'}</span>
                    </div>

                    {isGroupExpanded && byPeriod[pk].map(function (g, i) {
                      var gKey = groupKey + '|' + g.gstin
                      var isGExpanded = !!expandedGstins[gKey]
                      return (
                        <div key={g.gstin + '|' + i} style={{ marginBottom: '8px' }}>
                          <div
                            onClick={function () { toggleGstinRow(gKey) }}
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: '10px',
                              padding: '8px 16px',
                              background: C.white,
                              border: '1px solid ' + C.border,
                              borderRadius: '8px',
                              cursor: 'pointer',
                            }}
                          >
                            <span style={{
                              display: 'inline-block',
                              width: '12px',
                              fontSize: '10px',
                              color: C.text,
                              transform: isGExpanded ? 'rotate(90deg)' : 'rotate(0deg)',
                              transition: 'transform 0.18s',
                            }}>›</span>
                            <span style={{ fontSize: '14px', fontWeight: 400, color: C.text, fontFamily: C.sans }}>
                              GST number: {g.gstin}
                            </span>
                            {getStateFromGSTIN(g.gstin) && (
                              <span style={{
                                fontSize: '11px',
                                fontWeight: 600,
                                color: C.textMuted,
                                fontFamily: C.sans,
                                background: C.bg,
                                border: '1px solid ' + C.border,
                                borderRadius: '4px',
                                padding: '2px 8px',
                              }}>{getStateFromGSTIN(g.gstin)}</span>
                            )}
                            <span style={{
                              fontSize: '12px',
                              color: C.textLight,
                              fontFamily: C.sans,
                              marginLeft: 'auto',
                            }}>{isGExpanded ? 'Click to collapse' : 'Click to expand'}</span>
                          </div>
                          {isGExpanded && (
                            <div style={{ marginTop: '8px' }}>
                              <GSTRecordsUI
                                context={context}
                                account={account}
                                gstReports={g}
                                gstReportMonthlyList={[]}
                                dateRange={dateRange}
                              />
                            </div>
                          )}
                        </div>
                      )
                    })}
                  </div>
                )
              })
            })()}
            {gstReportMonthlyList && gstReportMonthlyList.length > 0 && (
              <div style={{ marginTop: '8px' }}>
                <div
                  onClick={function () { toggleFYSection(MONTHLY_KEY) }}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '10px',
                    padding: '8px 16px',
                    marginBottom: '8px',
                    background: C.accentBg,
                    border: '1px solid ' + C.accentBorder,
                    borderRadius: '8px',
                    cursor: 'pointer',
                  }}
                >
                  <span style={{
                    display: 'inline-block',
                    width: '12px',
                    fontSize: '10px',
                    color: '#3b82f6',
                    transform: monthlySummaryExpanded ? 'rotate(90deg)' : 'rotate(0deg)',
                    transition: 'transform 0.18s',
                  }}>›</span>
                  <span style={{
                    fontSize: '15px',
                    fontWeight: 400,
                    color: '#3b82f6',
                    fontFamily: C.sans,
                  }}>Monthly Summary — All Customers/Suppliers</span>
                  <span style={{
                    fontSize: '10px',
                    color: C.textLight,
                    fontFamily: C.sans,
                    marginLeft: 'auto',
                  }}>{monthlySummaryExpanded ? 'Click to collapse' : 'Click to expand'}</span>
                </div>

                {monthlySummaryExpanded && (function () {
                  var monthlyByGstin = {}
                  filteredGstReportMonthlyList.forEach(function (report) {
                    var g = report.gstin || 'Unknown'
                    if (!monthlyByGstin[g]) monthlyByGstin[g] = []
                    monthlyByGstin[g].push(report)
                  })
                  return Object.keys(monthlyByGstin).map(function (gstin) {
                    var rowKey = 'monthly|' + gstin
                    var isGstinExpanded = !!expandedGstins[rowKey]
                    return (
                      <div key={gstin} style={{ marginBottom: '8px' }}>
                        <div
                          onClick={function () { toggleGstinRow(rowKey) }}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '10px',
                            padding: '8px 16px',
                            background: C.white,
                            border: '1px solid ' + C.border,
                            borderRadius: '8px',
                            cursor: 'pointer',
                          }}
                        >
                          <span style={{
                            display: 'inline-block',
                            width: '12px',
                            fontSize: '10px',
                            color: C.text,
                            transform: isGstinExpanded ? 'rotate(90deg)' : 'rotate(0deg)',
                            transition: 'transform 0.18s',
                          }}>›</span>
                          <span style={{
                            fontSize: '14px',
                            fontWeight: 400,
                            color: C.text,
                            fontFamily: C.sans,
                          }}>GST number: {gstin}</span>
                          <span style={{
                            fontSize: '12px',
                            color: C.textLight,
                            fontFamily: C.sans,
                            marginLeft: 'auto',
                          }}>{isGstinExpanded ? 'Click to collapse' : 'Click to expand'}</span>
                        </div>

                        {isGstinExpanded && (function () {
                          var byFY = {}
                          monthlyByGstin[gstin].forEach(function (report) {
                            var fyLabel = getFYFromDateISO(report.dateISO)
                            if (!byFY[fyLabel]) byFY[fyLabel] = []
                            byFY[fyLabel].push(report)
                          })
                          var fyLabels = Object.keys(byFY).sort().reverse()
                          var viewMoreKey = 'viewMoreYears|' + gstin
                          var isViewMoreOn = !!viewMoreYears[viewMoreKey]
                          var visibleFYLabels = isViewMoreOn ? fyLabels : fyLabels.slice(0, 3)
                          var hiddenCount = fyLabels.length - 3
                          return (
                            <div style={{ marginTop: '8px' }}>
                              {visibleFYLabels.map(function (fyLabel) {
                                var fyRowKey = 'monthlyFY|' + gstin + '|' + fyLabel
                                var isFYExpanded = !!expandedGstins[fyRowKey]
                                return (
                                  <div key={fyLabel} style={{ marginBottom: '8px' }}>
                                    <div
                                      onClick={function () { toggleGstinRow(fyRowKey) }}
                                      style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '10px',
                                        padding: '8px 16px',
                                        background: C.bg,
                                        border: '1px solid ' + C.borderLight,
                                        borderRadius: '8px',
                                        cursor: 'pointer',
                                      }}
                                    >
                                      <span style={{
                                        display: 'inline-block',
                                        width: '12px',
                                        fontSize: '10px',
                                        color: C.text,
                                        transform: isFYExpanded ? 'rotate(90deg)' : 'rotate(0deg)',
                                        transition: 'transform 0.18s',
                                      }}>›</span>
                                      <span style={{
                                        fontSize: '13px',
                                        fontWeight: 400,
                                        color: C.text,
                                        fontFamily: C.sans,
                                      }}>{fyLabel}</span>
                                      <span style={{
                                        fontSize: '12px',
                                        color: C.textLight,
                                        fontFamily: C.sans,
                                        marginLeft: 'auto',
                                      }}>{isFYExpanded ? 'Click to collapse' : 'Click to expand'}</span>
                                    </div>

                                    {isFYExpanded && (
                                      <div style={{
                                        marginTop: '6px',
                                        background: C.white,
                                        border: '1px solid ' + C.border,
                                        borderRadius: '10px',
                                        overflow: 'hidden',
                                      }}>
                                        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                                          <thead>
                                            <tr>
                                              {['Period', 'Purchase', 'Purchase Tax', 'Sales', 'Sales Tax'].map(function (h, i) {
                                                return (
                                                  <th key={i} style={{
                                                    padding: '8px 14px',
                                                    fontSize: '14px',
                                                    fontWeight: 400,
                                                    color: C.text,
                                                    textAlign: i === 0 ? 'left' : 'right',
                                                    borderBottom: '1px solid ' + C.border,
                                                    background: C.bg,
                                                    whiteSpace: 'nowrap',
                                                    fontFamily: C.sans,
                                                  }}>{h}</th>
                                                )
                                              })}
                                            </tr>
                                          </thead>
                                          <tbody>
                                            {byFY[fyLabel].map(function (report, i) {
                                              return (
                                                <tr key={i} style={{
                                                  borderBottom: '1px solid ' + C.borderLight,
                                                  background: C.white,
                                                }}>
                                                  <td style={{
                                                    padding: '9px 14px',
                                                    fontSize: '14px',
                                                    fontFamily: C.sans,
                                                    color: C.textMuted,
                                                    whiteSpace: 'nowrap',
                                                  }}>{report.dateISO}</td>
                                                  {[report.purchaseF4, report.purchaseTaxF4, report.salesF4, report.salesTaxF4].map(function (val, j) {
                                                    return (
                                                      <td key={j} style={{
                                                        padding: '9px 14px',
                                                        fontSize: '14px',
                                                        textAlign: 'right',
                                                        fontFamily: C.sans,
                                                        color: C.text,
                                                        whiteSpace: 'nowrap',
                                                      }}>{val}</td>
                                                    )
                                                  })}
                                                </tr>
                                              )
                                            })}
                                          </tbody>
                                        </table>
                                      </div>
                                    )}
                                  </div>
                                )
                              })}
                              {hiddenCount > 0 && (
                                <div
                                  onClick={function () { toggleViewMoreYears(viewMoreKey) }}
                                  style={{
                                    padding: '8px 16px',
                                    fontSize: '13px',
                                    color: '#3b82f6',
                                    fontFamily: C.sans,
                                    cursor: 'pointer',
                                    textAlign: 'center',
                                  }}
                                >
                                  {isViewMoreOn ? 'View less' : 'View more (' + hiddenCount + ' more year' + (hiddenCount > 1 ? 's' : '') + ')'}
                                </div>
                              )}
                            </div>
                          )
                        })()}
                      </div>
                    )
                  })
                })()}
              </div>
            )}
          </div>

          <div style={{
            position: 'absolute',
            top: 0,
            right: 0,
            display: 'flex',
            flexWrap: 'wrap',
            gap: '6px',
            maxWidth: '340px',
            justifyContent: 'flex-end',
          }}>
            <span
              onClick={function () { setSelectedGstins(null) }}
              style={{
                padding: '6px 12px',
                fontSize: '12px',
                fontFamily: C.sans,
                fontWeight: selectedGstins === null ? 500 : 400,
                color: selectedGstins === null ? '#3b82f6' : C.text,
                background: selectedGstins === null ? C.bg : C.white,
                border: '1px solid ' + (selectedGstins === null ? '#3b82f6' : C.border),
                borderRadius: '20px',
                cursor: 'pointer',
                whiteSpace: 'nowrap',
              }}
            >All GSTs</span>
            {uniqueGstins.map(function (gstin, i) {
              var isChecked = isGstinSelected(gstin) && selectedGstins !== null
              return (
                <span
                  key={gstin}
                  onClick={function () { toggleGstinSelection(gstin) }}
                  style={{
                    padding: '6px 12px',
                    fontSize: '12px',
                    fontFamily: C.sans,
                    fontWeight: isChecked ? 500 : 400,
                    color: isChecked ? '#3b82f6' : C.text,
                    background: isChecked ? C.bg : C.white,
                    border: '1px solid ' + (isChecked ? '#3b82f6' : C.border),
                    borderRadius: '20px',
                    cursor: 'pointer',
                    whiteSpace: 'nowrap',
                  }}
                >GST No. {i + 1}</span>
              )
            })}
          </div>
        </div>
      )}
    </SuperUserAccountPageUI>
  )
}

export default GSTReportPageUI